/**
 * EXE (Ban GĐ) capability catalog — thin mirror of Field chrome pattern.
 * SoT: docs/Tai-lieu-trien-khai/FE_Exe_Channel_Parity_Plan.md
 */

import { PermissionCodes } from "@/shared/auth/permissions";

export type ExeDomain = "executive" | "sys";

export type ExeCapabilityStatus = "live" | "planned";

export type ExePlacement = "bottom-nav" | "account-hub" | "home-widget";

export type ExeAccountGroup = "account" | "work";

export type ExeCapabilityIcon =
  | "home"
  | "check-square"
  | "alert-triangle"
  | "bar-chart-3"
  | "user"
  | "user-round"
  | "bell"
  | "settings"
  | "history";

export type ExeCapability = {
  id: string;
  domain: ExeDomain;
  label: string;
  path: string;
  screenId?: string;
  permissions: string[];
  status: ExeCapabilityStatus;
  placement: ExePlacement[];
  accountGroup?: ExeAccountGroup;
  detail?: string;
  icon: ExeCapabilityIcon;
};

export const EXE_ACCOUNT_GROUP_LABEL: Record<ExeAccountGroup, string> = {
  account: "Tài khoản",
  work: "Công việc",
};

export const exeCapabilityRegistry: ExeCapability[] = [
  {
    id: "exe.home",
    domain: "executive",
    label: "Trang chủ",
    path: "/exe",
    screenId: "SCR-EXE-HOME",
    permissions: [PermissionCodes.ExeKpiRead],
    status: "live",
    placement: ["bottom-nav", "home-widget"],
    icon: "home",
  },
  {
    id: "exe.approvals",
    domain: "executive",
    label: "Duyệt",
    path: "/exe/approvals",
    screenId: "SCR-EXE-APPROVALS",
    permissions: [PermissionCodes.ExeApprovalAct],
    status: "live",
    placement: ["bottom-nav", "home-widget"],
    icon: "check-square",
  },
  {
    id: "exe.alerts",
    domain: "executive",
    label: "Cảnh báo",
    path: "/exe/alerts",
    screenId: "SCR-EXE-ALERTS",
    permissions: [PermissionCodes.ExeKpiRead],
    status: "live",
    placement: ["bottom-nav", "home-widget"],
    icon: "alert-triangle",
  },
  {
    id: "exe.reports",
    domain: "executive",
    label: "Báo cáo",
    path: "/exe/reports",
    screenId: "SCR-EXE-REPORTS",
    permissions: [PermissionCodes.ExeKpiRead],
    status: "live",
    placement: ["bottom-nav"],
    icon: "bar-chart-3",
  },
  {
    id: "exe.account",
    domain: "sys",
    label: "Tài khoản",
    path: "/exe/me",
    screenId: "SCR-EXE-ACCOUNT",
    permissions: [],
    status: "live",
    placement: ["bottom-nav"],
    icon: "user",
  },
  {
    id: "exe.sys.profile",
    domain: "sys",
    label: "Hồ sơ",
    path: "/exe/profile",
    screenId: "SCR-EXE-PROFILE",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    icon: "user-round",
  },
  {
    id: "exe.sys.notify",
    domain: "sys",
    label: "Thông báo",
    path: "/exe/notifications",
    screenId: "SCR-EXE-NOTIFY",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    icon: "bell",
  },
  {
    id: "exe.sys.settings",
    domain: "sys",
    label: "Cài đặt",
    path: "/exe/settings",
    screenId: "SCR-EXE-SETTINGS",
    permissions: [],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "account",
    icon: "settings",
  },
  {
    id: "exe.approvals.hub",
    domain: "executive",
    label: "Phiếu chờ duyệt",
    path: "/exe/approvals",
    screenId: "SCR-EXE-APPROVALS",
    permissions: [PermissionCodes.ExeApprovalAct],
    status: "live",
    placement: ["account-hub"],
    accountGroup: "work",
    detail: "Hàng đợi phê duyệt",
    icon: "check-square",
  },
  {
    id: "exe.qms.inbox",
    domain: "executive",
    label: "Chất lượng",
    path: "/exe/qms",
    screenId: "SCR-EXE-QMS",
    permissions: [PermissionCodes.ExeKpiRead],
    status: "live",
    placement: ["account-hub", "home-widget"],
    accountGroup: "work",
    detail: "Kiểm tra chất lượng đầu vào (IQC) & đầu ra (FQC)",
    icon: "check-square",
  },
];

export function exeAccountHubGroups(
  permissions: readonly string[],
  hasPerm: (granted: readonly string[], required: string[]) => boolean,
): { group: ExeAccountGroup; label: string; items: ExeCapability[] }[] {
  const live = exeCapabilityRegistry.filter(
    (c) =>
      c.status === "live" &&
      c.placement.includes("account-hub") &&
      (c.permissions.length === 0 || hasPerm(permissions, c.permissions)),
  );
  const order: ExeAccountGroup[] = ["account", "work"];
  return order
    .map((group) => ({
      group,
      label: EXE_ACCOUNT_GROUP_LABEL[group],
      items: live.filter((c) => c.accountGroup === group),
    }))
    .filter((g) => g.items.length > 0);
}
